import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Chess, PieceSymbol, Square } from 'chess.js';
import ChessBoard from '../ChessBoard/ChessBoard';
import styles from './CoordinateChallenge.module.css';
import { formatTime } from '../../../utils/timeUtils';
import { randomizePositions } from '../../../utils/chessRandomUtils';
import { KnowledgeItem, SuperMemoGrade } from '../../../types/learning';
import { ChallengeResult, mapResultToSuperMemoGrade, handleChallengeComplete } from '../../../utils/learningGrading';

import { useMissionDebugState } from '../../../hooks/useMissionDebugState';
import { useMissionDebugAction } from '../../../hooks/useMissionDebugAction';


/**
 * Props for test mode configuration
 */
interface TestModeProps {
  disableTimer?: boolean;
  initialCoordinates?: string[];
  autoComplete?: boolean;
  forceInterference?: boolean;
  disableInterference?: boolean;
  cooldownMs?: number;
  feedbackDurationMs?: number;
}

/**
 * Props for the CoordinateChallenge component
 */
interface CoordinateChallengeProps {
  timeLimit?: number;
  targetScore?: number;
  difficulty?: number;
  challengeCount?: number;
  missionId?: string;
  /**
   * Called when the challenge is complete. Deprecated: use onComplete(grade) instead.
   */
  onCompleteLegacy?: (success: boolean, score: number, accuracy: number) => void;
  /**
   * Called with the SuperMemo grade when the challenge is complete.
   */
  onComplete?: (grade: SuperMemoGrade) => void;
  /**
   * The knowledge item for this challenge (optional).
   */
  item?: KnowledgeItem;
  /**
   * Optional override for grading logic.
   */
  gradeMapper?: (result: ChallengeResult) => SuperMemoGrade;
  title?: string;
  testModeProps?: TestModeProps;
  randomizePiecesOnSuccess?: boolean;
}

/**
 * A chess coordinate challenge component
 * Displays a chessboard and asks the user to find specific coordinates
 */
export const CoordinateChallenge: React.FC<CoordinateChallengeProps> = ({
  timeLimit = 60,
  targetScore = 10,
  difficulty,
  challengeCount,
  missionId,
  onCompleteLegacy = () => {},
  onComplete,
  item,
  gradeMapper,
  title = 'Coordinate Challenge',
  testModeProps,
  randomizePiecesOnSuccess
}) => {
  // --- CALLBACKS THAT MUST BE DECLARED BEFORE HOOKS FOR CLOSURE SAFETY ---
  /**
   * Handles challenge completion and grading. Must be declared before any useEffect or hook that references it.
   * This is required for closure safety and to follow React's rules of hooks.
   */
  const onChallengeComplete = useCallback((success: boolean, correctCount: number, total: number, wrongCount: number) => {
    const attemptsForAccuracy = Math.max(1, correctCount + wrongCount);
    const accuracy = Number(((correctCount / attemptsForAccuracy) * 100).toFixed(1));
    const baseScore = total > 0 ? (correctCount / total) * 100 : 0;
    const missionScore = Math.max(0, Math.min(100, Math.round(baseScore - wrongCount * 2)));

    handleChallengeComplete({
      success,
      score: correctCount,
      total,
      gradeMapper,
      onComplete,
    });
    if (onCompleteLegacy) {
      onCompleteLegacy(success, missionScore, accuracy);
    }
  }, [gradeMapper, onComplete, onCompleteLegacy]);

  // Game state
  const [targetCoordinate, setTargetCoordinate] = useMissionDebugState<string>('', 'targetCoordinate', { label: 'Target Coordinate', type: 'string' });
  const [usedCoordinates, setUsedCoordinates] = useState<Set<string>>(new Set());
  const [score, setScore] = useState<number>(0);
  const [timeRemaining, setTimeRemaining] = useMissionDebugState<number>(timeLimit,'timeRemaining', { label: 'Time Remaining', type: 'number' });
  const [gameComplete, setGameComplete] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [wrongAttempts, setWrongAttempts] = useState<number>(0);
  const [currentFen, setCurrentFen] = useMissionDebugState<string>(new Chess().fen(), 'currentFen', { label: 'Current FEN', type: 'string' });
  
  // Feedback
  const [feedback, setFeedback] = useState<string>('');
  const [feedbackType, setFeedbackType] = useState<'correct' | 'incorrect' | null>(null);
  
  // Square highlight for clicked square
  const [highlightSquare, setHighlightSquare] = useState<string | undefined>(undefined);
  
  // Interference effects (for mission-103)
  const [interference, setInterference] = useState<boolean>(false);
  const [scrambled, setScrambled] = useState<boolean>(false);
  const [boardDistortion, setBoardDistortion] = useState<boolean>(false);

  // Cooldown to prevent rapid clicking
  const cooldownRef = useRef<boolean>(false);
  
  // Chess instance
  const chess = new Chess();
  
  // All possible coordinates
  const files = useMemo(() => ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'], []);
  const ranks = useMemo(() => ['1', '2', '3', '4', '5', '6', '7', '8'], []);
  const allCoordinates = useMemo(() => files.flatMap((file: string) => ranks.map((rank: string) => `${file}${rank}`)), [files, ranks]);
  
  /**
   * Generate a random coordinate
   * Returns a coordinate that hasn't been used yet if possible
   * @param usedCoords Set of already used coordinates
   * @returns A new coordinate
   */
  const generateRandomCoordinate = useCallback((usedCoords: Set<string>): string => {
    // For test mode, use provided coordinates
    if (testModeProps?.initialCoordinates && testModeProps.initialCoordinates.length > score) {
      return testModeProps.initialCoordinates[score];
    }
    
    // If all coordinates have been used or we're almost done, allow reusing coordinates
    if (usedCoords.size >= allCoordinates.length || usedCoords.size >= allCoordinates.length - 3) {
      // Just pick any random coordinate
      const file = files[Math.floor(Math.random() * files.length)];
      const rank = ranks[Math.floor(Math.random() * ranks.length)];
      return `${file}${rank}`;
    }
    
    // Generate a new coordinate
    let attempts = 0;
    let coordinate: string;
    
    // Try to find an unused coordinate with a maximum number of attempts
    do {
      const file = files[Math.floor(Math.random() * files.length)];
      const rank = ranks[Math.floor(Math.random() * ranks.length)];
      coordinate = `${file}${rank}`;
      attempts++;
    } while (usedCoords.has(coordinate) && attempts < 20);
    
    // If we couldn't find an unused coordinate after several attempts, just return any coordinate
    return coordinate;
  }, [files, ranks, allCoordinates, score, testModeProps]);
  
  /**
   * Generates a random chess position
   * @returns A valid FEN string representing a random position
   */
  // Use randomizePositions from src/utils/chessRandomUtils
  
  /**
   * Check if mission should have randomized pieces
   */
  const getShouldMissionBeRandomized = useCallback(() => {
    switch (missionId) {
      case 'coordinates-103':
      case 'coordinates-102':
        return true;
      default:
        return false;
    }
  }, [missionId]);
  
  // Set initial coordinate and position - Run only once on mount
  useEffect(() => {
    const initialCoordinate = generateRandomCoordinate(new Set());
    setTargetCoordinate(initialCoordinate);
    setUsedCoordinates(new Set([initialCoordinate]));
    
    // Set initial random position if needed
    if (getShouldMissionBeRandomized() || randomizePiecesOnSuccess) {
      setCurrentFen(randomizePositions());
    }
  }, [generateRandomCoordinate, getShouldMissionBeRandomized, randomizePiecesOnSuccess, setCurrentFen, setTargetCoordinate]);
  
  // Timer effect
  useEffect(() => {
    if (gameComplete || (testModeProps?.disableTimer)) return;
    
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setGameComplete(true);
          setSuccess(score >= targetScore);
          onChallengeComplete(score >= targetScore, score, targetScore, wrongAttempts);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [gameComplete, targetScore, score, onChallengeComplete, testModeProps, setTimeRemaining, wrongAttempts]);
  
  // Add interference effects for mission-103
  useEffect(() => {
    if (missionId !== 'coordinates-103' || testModeProps?.disableInterference) return;
    
    // Ensure we apply effects regardless of random condition during initial load
    // to make sure they're visible for testing
    setInterference(true);
    setTimeout(() => setInterference(false), 2000);
    
    // Also show scrambled effect on initial load
    setScrambled(true);
    setTimeout(() => setScrambled(false), 1500);
    
    // Show initial board distortion effect - extended duration
    setBoardDistortion(true);
    setTimeout(() => setBoardDistortion(false), 2500);
    
    // Randomly apply interference effects
    const interferenceInterval = setInterval(() => {
      // Increase probability from 0.2 to 0.4 to make effects more noticeable
      if (Math.random() < 0.95) {
        setInterference(true);
        setTimeout(() => setInterference(false), 1500);
      }
    }, 500 + Math.random() * 500);
    
    // Randomly scramble coordinates - more frequently (every 3-4 seconds instead of 7)
    const scrambleInterval = setInterval(() => {
      // Increase probability from 0.5 to 0.7 to make scrambling happen more often
      if (Math.random() < 0.95) {
        setScrambled(true);
        setTimeout(() => setScrambled(false), 500); // Shorter duration for more frequent glitches
      }
    }, 500 + Math.random() * 500); // Random interval between 3-4 seconds
    
    // Randomly apply board distortion effects - increased frequency and duration
    const boardDistortionInterval = setInterval(() => {
      if (Math.random() < 0.6) { // Increased probability from 0.3 to 0.6
        setBoardDistortion(true);
        setTimeout(() => setBoardDistortion(false), 2500); // Increased duration
      }
    }, 3000 + Math.random() * 2000); // Decreased interval: 3-5 seconds (from 6-8)
    
    return () => {
      clearInterval(interferenceInterval);
      clearInterval(scrambleInterval);
      clearInterval(boardDistortionInterval);
    };
  }, [missionId, testModeProps?.disableInterference]);
  
  // Handle test mode auto-complete
  useEffect(() => {
    if (testModeProps?.autoComplete && score >= targetScore) {
      setGameComplete(true);
      setSuccess(true);
      onChallengeComplete(true, score, targetScore, wrongAttempts);
    }
  }, [score, targetScore, testModeProps, onChallengeComplete, wrongAttempts]);
  
  /**
   * Handle square click
   * Check if the clicked square matches the target coordinate
   */
  const handleSquareClick = (square: string) => {
    if (gameComplete || cooldownRef.current) return;
    
    // Set cooldown to prevent rapid clicking
    cooldownRef.current = true;
    const cooldownMs = testModeProps?.cooldownMs ?? 500;
    setTimeout(() => {
      cooldownRef.current = false;
    }, cooldownMs);
    
    if (square === targetCoordinate) {
      // Correct answer
      setScore(prev => prev + 1);
      setFeedback('Correct!');
      setFeedbackType('correct');
      setHighlightSquare(`${square}:correct`);
      
      // Check if game is complete
      if (score + 1 >= targetScore) {
        setGameComplete(true);
        setSuccess(true);
        onChallengeComplete(true, score + 1, targetScore, wrongAttempts);
        return;
      }
      
      // Generate next coordinate and update used coordinates
      setUsedCoordinates(prev => {
        const newSet = new Set(prev);
        
        // Reset if almost all coordinates have been used
        if (newSet.size >= allCoordinates.length - 5) {
          const nextCoord = generateRandomCoordinate(new Set());
          setTargetCoordinate(nextCoord);
          return new Set([nextCoord]);
        } else {
          const nextCoord = generateRandomCoordinate(newSet);
          newSet.add(nextCoord);
          setTargetCoordinate(nextCoord);
          return newSet;
        }
      });

      // Randomize piece positions if enabled
      if (getShouldMissionBeRandomized() || randomizePiecesOnSuccess) {
        const newPosition = randomizePositions();
        setCurrentFen(newPosition);
      }

      // Clear the highlight after a delay
      const feedbackDurationMs = testModeProps?.feedbackDurationMs ?? 1500;
      setTimeout(() => {
        setHighlightSquare(undefined);
      }, feedbackDurationMs);
    } else {
      // Incorrect answer
      setFeedback('Try Again!');
      setFeedbackType('incorrect');
      setHighlightSquare(`${square}:incorrect`);
      setWrongAttempts(prev => prev + 1);
      
      // Clear the highlight after a delay
      const feedbackDurationMs = testModeProps?.feedbackDurationMs ?? 1500;
      setTimeout(() => {
        setHighlightSquare(undefined);
      }, feedbackDurationMs);
    }
    
    // Clear feedback after a delay
    const feedbackDurationMs = testModeProps?.feedbackDurationMs ?? 1500;
    setTimeout(() => {
      setFeedback('');
      setFeedbackType(null);
    }, feedbackDurationMs);
  };
  
  // Mission-specific title
  const getMissionTitle = () => {
    switch (missionId) {
      case 'coordinates-101':
        return 'Mission: Basic Coordinates Training';
      case 'coordinates-102':
        return 'Mission: Advanced Coordinates';
      case 'coordinates-103':
        return 'Mission: Jamming Signal Detected';
      default:
        return title;
    }
  };


  

  // Force interference effects for testing using testModeProps
  useEffect(() => {
    if (testModeProps?.forceInterference) {
      setInterference(true);
      setTimeout(() => setInterference(false), 2000);
    }
  }, [testModeProps?.forceInterference]);

  return (
    <div 
      className={`${styles.container} ${interference ? styles.interference : ''}`}
      data-testid="coordinate-challenge-container"
      style={{ position: 'relative' }}
    >
      <div className={styles.header}>
        <h2>{getMissionTitle()}</h2>
        <div className={styles.stats}>
          <span className={styles.score}>Score: {score}/{targetScore}</span>
          <span className={styles.timer}>Time: {formatTime(timeRemaining)}</span>
        </div>
      </div>
      
      {!gameComplete ? (
        <>
          <div className={styles.target}>
            <p>Find this coordinate:</p>
            <span 
              className={`${styles.coordinate} ${scrambled ? styles.scrambled : ''}`}
              data-testid="target-coordinate" 
              data-content={targetCoordinate}
            >
              {targetCoordinate}
            </span>
          </div>
          
          <div className={`${styles.boardContainer} ${boardDistortion ? styles.boardDistortion : ''}`}
               data-testid="board-container">
            <ChessBoard 
              size={Math.min(400, window.innerWidth - 40)}
              fen={currentFen}
              onSquareClick={handleSquareClick}
              showCoordinates={true}
              allowMoves={false}
              highlightSquare={highlightSquare}
            />
          </div>
          
          {feedback && (
            <div className={`${styles.feedback} ${styles[feedbackType || '']}`}>
              {feedback}
            </div>
          )}
        </>
      ) : (
        <div className={styles.completion}>
          <h3>{success ? 'Challenge Completed!' : 'Challenge Failed!'}</h3>
          <p>You scored {score} out of {targetScore} points</p>
          {!success && <p>Try again to improve your score!</p>}
        </div>
      )}
    </div>
  );
};

export default CoordinateChallenge; 
